/**
 * personal-review.ts — 個人化複習卷組卷器(C:接在 A 自動批改 + B 會教錯題本之後)。
 *
 * 取代舊「生成複習考卷」對 worker LLM-Wiki 的依賴:改成「純學習側、離線可組」的
 * 個人化複習卷。資料來源都在本機 `PlayerProgress`:
 *
 *   1. `progress.mistakes`(未 retried)— 真正寫錯的題,最該複習。
 *   2. `progress.studentKPs`(Leitner box 低 / 已到期)— 系統判定的弱點。
 *
 * 流程(對應 task C):
 *   pickWeakKPs()           跨科挑「最該複習」的弱點 KP(box 低 > 最近錯 > 錯次多)。
 *   buildPersonalReviewPaper()  每個弱 KP = 原錯題本人 + 1~2 題類似題
 *                           (數學走 generator 答案確定;其他科走 LLM,LLM 不可用時
 *                            優雅降級成只放原錯題)。輸出按科分大題的考卷結構。
 *
 * 鐵則(CLAUDE.md / wiki page-470f59c0):
 *   - **只動學習側資料**(mistakes / studentKPs / factionXP / box),絕不碰戰鬥數值、
 *     commanderXp。本模組只「讀」progress,不寫;批改回流由呼叫端做。
 *   - 輸出永遠是「考卷形式」(分科大題 + 題號 + 作答格),不是一問一答對話。
 *   - 向後相容:沒有 studentKPs / 空錯題本都要優雅處理,不可炸舊存檔。
 *
 * 與既有型別的關係:`PersonalReviewPaper` 可經 `toReviewPaper()` 攤平成既有
 * `ReviewPaper`(types.ts),讓拍照批改 `grade-review` / `storeReviewPaper` 沿用,
 * 不必重寫批改;列印走 `generatePersonalReviewHtml`(分科大題)。
 */

import type { MistakeEntry, PlayerProgress, Subject } from '../../shared/types';
import { getMistakes } from '../../shared/mistake-log';
import { isDueByPaper } from '../../shared/student-kp';
import { generateSimilarQuestion, normalizeAnswer } from '../mistake-similar';
import type { ReviewPaper, ReviewQuestion } from './types';

/** 一張個人化複習卷預設覆蓋的弱點數量上限(避免太長,K-3 一次別超過 ~6 個 KP)。 */
export const DEFAULT_MAX_WEAK_KPS = 6;
/** 每個弱 KP 最多附幾題類似題(原錯題 + 這些)。 */
export const SIMILAR_PER_KP = 2;
/** Leitner box 低於此值視為「不熟」,優先複習(box 1~2 = 還在學)。 */
const WEAK_BOX_THRESHOLD = 3;

const SUBJECT_LABEL: Record<Subject, string> = {
  math: '數學',
  chinese: '國語',
  english: '英語',
  nature: '自然',
  social: '社會',
};

/** 大題排序固定順序(分科時用),讓同一份卷每次科目順序穩定。 */
const SUBJECT_ORDER: Subject[] = ['math', 'chinese', 'english', 'nature', 'social'];

/** 一個被挑出的弱點:某 KP + 它的代表錯題(若有)+ 弱點來源/強度資訊。 */
export interface WeakKP {
  kp: string;
  subject: Subject;
  /** 代表錯題(該 KP 最近一次寫錯的那題)。沒有對應錯題時為 null(純 box 弱點)。 */
  mistake: MistakeEntry | null;
  /** Leitner box(1~5);未建檔時視為 1(最不熟)。 */
  box: number;
  /** 這個 KP 累計被記錄為錯的次數(同 KP 多次錯 → 更該複習)。 */
  mistakeCount: number;
  /** 最近一次錯的時間戳(ms);純 box 弱點用 0。越新越優先。 */
  lastWrongAt: number;
  /** 弱點來源:'mistake'(來自錯題本)或 'box'(僅 studentKP 判定)。 */
  source: 'mistake' | 'box';
}

/** 複習卷的一道題(原錯題或類似題)。攤平成填答比對所需的最小資料。 */
export interface ReviewItem {
  kp: string;
  subject: Subject;
  prompt: string;
  /** 顯示用正解(揭示時用)。 */
  displayAnswer: string;
  /** 已正規化的可接受答案集合,用於即時 / 批改比對。 */
  acceptedAnswers: string[];
  /** 一句說明,怎麼算 / 為什麼(可能為空字串)。 */
  explanation: string;
  /** 這題是「原錯題本人」還是「類似題」。 */
  origin: 'mistake' | 'similar';
}

/** 一個分科大題:同一科目下的所有複習題。 */
export interface ReviewSection {
  subject: Subject;
  /** 大題標題,例:「數學」。 */
  label: string;
  items: ReviewItem[];
}

/** 一張個人化複習卷:分科大題 + 涵蓋的弱點 KP 清單。 */
export interface PersonalReviewPaper {
  paperId: string;
  generatedAtISO: string;
  studentName?: string;
  /** 按科分的大題(只含有題目的科目)。 */
  sections: ReviewSection[];
  /** 這張卷針對的弱點 KP 數(卷頭文案用)。 */
  weakKpCount: number;
  /** 是否走了降級(空錯題本 → 只用 box 弱點 / 或完全無弱點)。 */
  degraded: boolean;
}

/* ──────────────────────────────────────────────────────────────────────────
   1) 跨科挑弱點
   ────────────────────────────────────────────────────────────────────────── */

/**
 * 從錯題本 + studentKPs 跨科挑「最該複習」的弱點 KP。
 *
 * 合併規則:
 *   - 錯題本(未 retried)依 KP 聚合 → 每個 KP 取「最近一次錯」當代表錯題,
 *     記 mistakeCount / lastWrongAt。
 *   - studentKPs 中 box < 門檻 或「已到期」(`isDueByPaper`)的 KP 也納入弱點;
 *     若該 KP 已在錯題清單中,只補上 box 資訊,不重複。
 *
 * 排序(由強到弱,越前面越該複習):
 *   1. box 低者優先(越不熟越前)。
 *   2. 同 box → 最近錯的優先(lastWrongAt 大者前)。
 *   3. 再同 → 錯次多者優先(mistakeCount 大者前)。
 *
 * @returns 依優先序、長度 ≤ maxKps 的弱點清單。沒有任何弱點時回空陣列。
 */
export function pickWeakKPs(
  progress: PlayerProgress,
  maxKps: number = DEFAULT_MAX_WEAK_KPS,
): WeakKP[] {
  const byKp = new Map<string, WeakKP>();

  // (a) 錯題本聚合 —— 未 retried 才算「待複習」。
  const mistakes = getMistakes(progress, { excludeRetried: true });
  for (const m of mistakes) {
    const existing = byKp.get(m.kp);
    if (!existing) {
      byKp.set(m.kp, {
        kp: m.kp,
        subject: m.subject,
        mistake: m,
        box: boxForKp(progress, m.kp),
        mistakeCount: 1,
        lastWrongAt: m.timestamp,
        source: 'mistake',
      });
    } else {
      existing.mistakeCount += 1;
      // getMistakes 已 newest-first,故第一個遇到的就是最近;後面只累計次數。
      if (m.timestamp > existing.lastWrongAt) {
        existing.lastWrongAt = m.timestamp;
        existing.mistake = m;
      }
    }
  }

  // (b) studentKPs 弱點(box 低 / 到期)補進來(沒對應錯題的純 box 弱點)。
  const kps = progress.studentKPs ?? [];
  for (const kp of kps) {
    const weakByBox = kp.box < WEAK_BOX_THRESHOLD;
    const weakByDue = isDueByPaper(kp, progress);
    if (!weakByBox && !weakByDue) continue;

    const existing = byKp.get(kp.kpId);
    if (existing) {
      // 已有錯題代表;補正 box(以 studentKP 的真實 box 為準)。
      existing.box = kp.box;
      continue;
    }
    byKp.set(kp.kpId, {
      kp: kp.kpId,
      subject: kp.subject,
      mistake: null,
      box: kp.box,
      mistakeCount: 0,
      lastWrongAt: 0,
      source: 'box',
    });
  }

  const all = [...byKp.values()];
  all.sort((a, b) => {
    if (a.box !== b.box) return a.box - b.box; // box 低優先
    if (a.lastWrongAt !== b.lastWrongAt) return b.lastWrongAt - a.lastWrongAt; // 最近錯優先
    return b.mistakeCount - a.mistakeCount; // 錯次多優先
  });

  return all.slice(0, Math.max(0, maxKps));
}

/** 讀某 KP 的 Leitner box;未建檔回 1(最不熟)。 */
function boxForKp(progress: PlayerProgress, kpId: string): number {
  const found = progress.studentKPs?.find((k) => k.kpId === kpId);
  return found?.box ?? 1;
}

/* ──────────────────────────────────────────────────────────────────────────
   2) 組成個人化複習卷(每弱 KP = 原錯題 + 1~2 類似題)
   ────────────────────────────────────────────────────────────────────────── */

/**
 * 把一個弱 KP 組成複習題清單:原錯題本人(若有)+ 最多 `similarCount` 題類似題。
 *
 * 類似題走 `generateSimilarQuestion`(數學 generator 答案確定;其他科 LLM,
 * 不可用回 null)。LLM 不可用 / 解析失敗 → 該題略過(優雅降級):
 *   - 有原錯題 → 至少還有原錯題可練。
 *   - 純 box 弱點且生不出類似題 → 該 KP 不產任何題(由呼叫端聚合後忽略)。
 *
 * @param seedBase 給 generator 的種子基底;同 KP 多題用 seedBase+i 取不同題面。
 */
export async function buildItemsForKP(
  weak: WeakKP,
  similarCount: number = SIMILAR_PER_KP,
  seedBase: number = Date.now() & 0x7fffffff,
): Promise<ReviewItem[]> {
  const items: ReviewItem[] = [];

  // (a) 原錯題本人(放最前面,先讓孩子再做一次真正錯的那題)。
  if (weak.mistake) {
    items.push(mistakeToItem(weak.mistake));
  }

  // (b) 1~2 題類似題。每題用不同 seed,確保 generator 路徑題面不同。
  for (let i = 0; i < Math.max(0, similarCount); i++) {
    const refPrompt = weak.mistake?.prompt ?? '';
    const sim = await generateSimilarQuestion(
      weak.kp,
      weak.subject,
      refPrompt,
      seedBase + i * 7919, // 質數步距,降低同題碰撞。
    );
    if (!sim) continue; // 優雅降級:生不出就略過這題。
    items.push({
      kp: weak.kp,
      subject: weak.subject,
      prompt: sim.prompt,
      displayAnswer: sim.displayAnswer,
      acceptedAnswers: sim.acceptedAnswers,
      explanation: sim.explanation,
      origin: 'similar',
    });
  }

  return items;
}

/** 把一條錯題本 entry 攤平成複習題(原錯題)。 */
function mistakeToItem(m: MistakeEntry): ReviewItem {
  const correct = String(m.correctAnswer);
  return {
    kp: m.kp,
    subject: m.subject,
    prompt: m.prompt,
    displayAnswer: correct,
    acceptedAnswers: [normalizeAnswer(correct)],
    explanation: m.explanation?.trim() ?? '',
    origin: 'mistake',
  };
}

/**
 * 組一張完整的個人化複習卷:挑弱點 → 每弱點組題 → 按科分大題。
 *
 * 降級規則:
 *   - 完全沒有弱點(空錯題本 + 無弱 box)→ `sections` 空、`degraded: true`,
 *     呼叫端顯示「先去練習」友善提示。
 *   - 有弱點但全部生不出任何題(極少見)→ 同樣 sections 空、degraded: true。
 *   - 走到任何 box-only 弱點 → degraded: true(代表沒有真錯題墊底)。
 *
 * @returns PersonalReviewPaper(分科大題)。
 */
export async function buildPersonalReviewPaper(
  progress: PlayerProgress,
  opts: {
    maxKps?: number;
    similarPerKp?: number;
    /** 注入時鐘(測試用);預設 Date.now。 */
    now?: () => number;
  } = {},
): Promise<PersonalReviewPaper> {
  const now = opts.now ?? (() => Date.now());
  const nowMs = now();
  const maxKps = opts.maxKps ?? DEFAULT_MAX_WEAK_KPS;
  const similarPerKp = opts.similarPerKp ?? SIMILAR_PER_KP;

  const weakKps = pickWeakKPs(progress, maxKps);
  const usedBoxOnly = weakKps.some((w) => w.source === 'box');

  // 每個弱 KP 組題(各自獨立 seed 基底,以弱點順序 i 偏移)。
  const perKpItems = await Promise.all(
    weakKps.map((w, i) => buildItemsForKP(w, similarPerKp, (nowMs & 0x7fffffff) + i * 104729)),
  );

  // 按科聚合成大題(只保留有題目的科目;科目順序固定)。
  const bySubject = new Map<Subject, ReviewItem[]>();
  perKpItems.flat().forEach((item) => {
    const list = bySubject.get(item.subject) ?? [];
    list.push(item);
    bySubject.set(item.subject, list);
  });

  const sections: ReviewSection[] = SUBJECT_ORDER.filter((s) => bySubject.has(s)).map((s) => ({
    subject: s,
    label: SUBJECT_LABEL[s],
    items: bySubject.get(s)!,
  }));

  const totalItems = sections.reduce((n, s) => n + s.items.length, 0);
  const degraded = totalItems === 0 || usedBoxOnly;

  return {
    paperId: makePaperId(nowMs),
    generatedAtISO: new Date(nowMs).toISOString(),
    studentName: progress.studentName || undefined,
    sections,
    weakKpCount: weakKps.length,
    degraded,
  };
}

/** 卷號:R + base36 時間戳尾段,人類可讀、夠唯一(本機用)。 */
function makePaperId(nowMs: number): string {
  return 'R' + nowMs.toString(36).toUpperCase().slice(-6);
}

/* ──────────────────────────────────────────────────────────────────────────
   3) 攤平成既有 ReviewPaper(沿用 pdf-gen / grade-review / storeReviewPaper)
   ────────────────────────────────────────────────────────────────────────── */

/**
 * 把分科 `PersonalReviewPaper` 攤平成既有 `ReviewPaper`(types.ts)。
 *
 * 題號(`idx`)以「整卷連續」編(1..N,跨科累加),`subject`/`kp` 帶上讓拍照批改
 * `grade-review` 能正確回流到對應科目 / KP。`answerKey` 用每題第一個可接受答案
 * (已揭示用 displayAnswer 更友善,但 key 用 acceptedAnswers[0] 與比對一致)。
 *
 * 全部當 `fill` 題(複習卷不出選擇題,孩子手寫答案再批改),維持純文字作答流。
 */
export function toReviewPaper(paper: PersonalReviewPaper): ReviewPaper {
  const front: ReviewQuestion[] = [];
  const answerKey: { idx: number; correct: string }[] = [];
  let idx = 0;

  for (const section of paper.sections) {
    for (const item of section.items) {
      idx += 1;
      const correct = item.displayAnswer || item.acceptedAnswers[0] || '';
      front.push({
        idx,
        subject: item.subject,
        kp: item.kp,
        kind: 'fill',
        prompt: item.prompt,
        correct,
        fromMistake: item.origin === 'mistake',
      });
      answerKey.push({ idx, correct });
    }
  }

  return {
    paperId: paper.paperId,
    generatedAtISO: paper.generatedAtISO,
    studentName: paper.studentName,
    front,
    answerKey,
    sourceWikiPageIds: [],
  };
}

/** 卷頭文案:「個人化複習卷 · 針對 N 個弱點」。 */
export function paperHeadline(paper: PersonalReviewPaper): string {
  return `個人化複習卷 · 針對 ${paper.weakKpCount} 個弱點`;
}
